import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import countryList from 'react-select-country-list';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Field,
  Input,
  Select,
  Textarea,
} from '../design-system';
import useUser from "../hooks/useUser";
import fetchWithTimeout from '../utils/fetchWithTimeout';
import { buildAuthOverlayRouteState } from '../utils/authOverlayNavigation';
import AccountSettingsLoadingState from './AccountSettings/AccountSettingsLoadingState';
import {
  getAccountSettingsUserCacheKey,
  makeAccountSettingsCacheKey,
  readAccountSettingsCache,
  writeAccountSettingsCache,
} from './AccountSettings/accountSettingsDataCache';
import "./UserProfile.css";

interface ProfileUser {
  email?: string;
  picture?: string;
  nickname?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  location?: string;
  tradingExperience?: string;
  bio?: string;
  avatar?: string;
}

interface PersonalDetails {
  nickname: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  location: string;
  tradingExperience: string;
  bio: string;
  avatar: string;
}

interface ProfileResponse {
  profile?: Partial<PersonalDetails>;
}

interface SaveProfileResponse {
  errorCode?: keyof typeof errorMessagesByCode;
  message?: string;
}

interface ChangeEmailResponse {
  success?: boolean;
  newEmail?: string;
  emailMode?: "verify_email" | "set_password" | "none";
  error?: string;
  errorCode?: string;
  supportRequired?: boolean;
  result?: {
    newEmail?: string;
    surfaces?: {
      botspotDb?: {
        status?: string;
      };
    };
  };
}

interface CountryOption {
  value: string;
  label: string;
}

const errorMessagesByCode = {
  NICKNAME_INVALID_CHARACTERS: "Nickname has unsupported characters.",
  NICKNAME_LENGTH_INVALID: "Nickname length is invalid.",
  NICKNAME_RESERVED: "That nickname is reserved.",
  NICKNAME_EXISTS: "That nickname is already taken.",
} as const;

const emailChangeErrorMessages: Record<string, string> = {
  EMAIL_INVALID: "Enter a valid email address.",
  EMAIL_CONFIRMATION_MISMATCH: "The new email fields do not match.",
  CURRENT_EMAIL_MISMATCH: "Current email does not match this account.",
  EMAIL_ALREADY_EXISTS: "That email is already used by another account.",
  EMAIL_ALREADY_EXISTS_AUTH0: "That email is already used by another account.",
  FRESH_LOGIN_REQUIRED: "Log in again before changing your email.",
  PURCHASE_LOGIN_EXPIRED: "This login link is too old to change email.",
  CHECKOUT_ACCESS_REQUIRED: "This account needs support to change email.",
  EMAIL_CHANGE_SUPPORT_REQUIRED: "This login method needs support to change email.",
  EMAIL_CHANGE_LOGIN_METHOD_UNSUPPORTED: "This login method cannot change email here.",
  PARTIAL_EMAIL_CHANGE: "Email change needs support follow-up.",
  PARTIAL_EMAIL_CHANGE_EMAIL_FAILED: "Email changed, but the follow-up email failed.",
};

const initialProfile = (user?: ProfileUser | null): PersonalDetails => ({
  nickname: user?.nickname || "",
  email: user?.email || "",
  firstName: user?.firstName || "",
  lastName: user?.lastName || "",
  phone: user?.phone || "",
  location: user?.location || "",
  tradingExperience: user?.tradingExperience || "",
  bio: user?.bio || "",
  avatar: user?.avatar || user?.picture || "",
});

const AUTOSAVE_DEBOUNCE_MS = 500;
const PROFILE_REQUEST_TIMEOUT_MS = 10000;

const editableProfilePayload = ({ nickname, firstName, lastName, phone, location, tradingExperience, bio }: PersonalDetails) => ({
  nickname,
  firstName,
  lastName,
  phone,
  location,
  tradingExperience,
  bio,
});

const profileDetailsEqual = (left: PersonalDetails, right: PersonalDetails): boolean => (
  left.nickname === right.nickname
  && left.firstName === right.firstName
  && left.lastName === right.lastName
  && left.phone === right.phone
  && left.location === right.location
  && left.tradingExperience === right.tradingExperience
  && left.bio === right.bio
);

const UserProfile = (): React.ReactElement | null => {
  const {
    user,
    isAuthenticated,
    isLoading,
    authLoading,
    getApiToken,
    getFreshLoginIdToken,
    authMethod,
    loginWithRedirect,
    logout,
  } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthLoading = authLoading ?? isLoading;
  const profileCacheKey = makeAccountSettingsCacheKey(
    'profile',
    getAccountSettingsUserCacheKey(user),
    process.env.REACT_APP_API_URL || 'api',
  );
  const cachedProfile = readAccountSettingsCache<PersonalDetails>(profileCacheKey);
  const [personalDetails, setPersonalDetails] = useState(() => cachedProfile || initialProfile(user));
  const savedDetailsRef = useRef<PersonalDetails>(cachedProfile || initialProfile(user));
  const latestDetailsRef = useRef<PersonalDetails>(cachedProfile || initialProfile(user));
  const queuedDetailsRef = useRef<PersonalDetails | null>(null);
  const failedDetailsRef = useRef<PersonalDetails | null>(null);
  const savingRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(() => !cachedProfile);
  const [profileLoadError, setProfileLoadError] = useState("");
  const [saveErrorMsg, setSaveErrorMsg] = useState("");
  const [isEmailChangeOpen, setIsEmailChangeOpen] = useState(false);
  const [emailChangeForm, setEmailChangeForm] = useState({ newEmail: "" });
  const [emailChangeError, setEmailChangeError] = useState("");
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const countryOptions = useMemo<CountryOption[]>(() => countryList().getData(), []);

  useEffect(() => {
    if (user && !readAccountSettingsCache<PersonalDetails>(profileCacheKey)) {
      setPersonalDetails((prev) => {
        const next = {
          ...prev,
          ...initialProfile({ ...prev, ...user }),
        };
        latestDetailsRef.current = next;
        return next;
      });
    }
  }, [profileCacheKey, user]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isAuthLoading || isAuthenticated) return;
    navigate(
      `/login?returnTo=${encodeURIComponent('/account-settings/profile')}`,
      {
        replace: true,
        state: buildAuthOverlayRouteState(location),
      }
    );
  }, [isAuthenticated, isAuthLoading, location, navigate]);

  const fetchProfile = useCallback(async (
    signal?: AbortSignal,
    options: { force?: boolean } = {},
  ): Promise<void> => {
    if (!isAuthenticated || !user) return;
    const cached = readAccountSettingsCache<PersonalDetails>(profileCacheKey);
    if (cached && !options.force) {
      setPersonalDetails(cached);
      savedDetailsRef.current = cached;
      latestDetailsRef.current = cached;
      queuedDetailsRef.current = null;
      failedDetailsRef.current = null;
      setProfileLoadError("");
      setIsProfileLoading(false);
      return;
    }

    setIsProfileLoading(true);
    setProfileLoadError("");

    try {
      const token = await getApiToken();
      const response = await fetchWithTimeout(`${process.env.REACT_APP_API_URL}/users/user_profile`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
        signal,
      }, PROFILE_REQUEST_TIMEOUT_MS);

      if (!response.ok) {
        throw new Error(`Profile request failed with ${response.status}`);
      }

      const data = (await response.json()) as ProfileResponse;
      const profile = data.profile;
      if (!profile) return;

      const currentDetails = latestDetailsRef.current;
      const loadedDetails = {
        ...currentDetails,
        email: profile.email || currentDetails.email || "",
        nickname: profile.nickname || "",
        firstName: profile.firstName || "",
        lastName: profile.lastName || "",
        phone: profile.phone || "",
        location: profile.location || "",
        tradingExperience: profile.tradingExperience || "",
        bio: profile.bio || "",
        avatar: profile.avatar || currentDetails.avatar,
      };
      setPersonalDetails(loadedDetails);
      savedDetailsRef.current = loadedDetails;
      latestDetailsRef.current = loadedDetails;
      queuedDetailsRef.current = null;
      failedDetailsRef.current = null;
      writeAccountSettingsCache(profileCacheKey, loadedDetails);
    } catch {
      if (!signal?.aborted) {
        setProfileLoadError("Profile details did not load. Retry before saving so existing saved values are not overwritten.");
      }
    } finally {
      if (!signal?.aborted) {
        setIsProfileLoading(false);
      }
    }
  }, [getApiToken, isAuthenticated, profileCacheKey, user]);

  useEffect(() => {
    if (!isAuthenticated || !user) return undefined;
    const controller = new AbortController();
    fetchProfile(controller.signal);
    return () => {
      controller.abort();
    };
  }, [fetchProfile, isAuthenticated, user]);

  const clearProfileDebounce = (): void => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  };

  const drainProfileSaveQueue = useCallback(async (): Promise<void> => {
    if (savingRef.current || isProfileLoading || profileLoadError) return;
    const candidate = queuedDetailsRef.current;
    if (!candidate || profileDetailsEqual(candidate, savedDetailsRef.current)) {
      queuedDetailsRef.current = null;
      return;
    }

    queuedDetailsRef.current = null;
    savingRef.current = true;
    setIsSavingProfile(true);
    setSaveErrorMsg("");
    try {
      const token = await getApiToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/user_profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editableProfilePayload(candidate)),
      });

      const data = (await response.json()) as SaveProfileResponse;
      if (response.ok) {
        savedDetailsRef.current = candidate;
        failedDetailsRef.current = null;
        writeAccountSettingsCache(profileCacheKey, candidate);
        if (!profileDetailsEqual(latestDetailsRef.current, candidate)) {
          queuedDetailsRef.current = latestDetailsRef.current;
        }
      } else {
        const displayMsg = data.errorCode
          ? errorMessagesByCode[data.errorCode]
          : data.message || "Profile auto-save failed.";
        failedDetailsRef.current = candidate;
        setSaveErrorMsg(displayMsg);
      }
    } catch {
      failedDetailsRef.current = candidate;
      setSaveErrorMsg("Profile auto-save failed. Check your connection.");
    } finally {
      savingRef.current = false;
      setIsSavingProfile(false);
      if (queuedDetailsRef.current) {
        void drainProfileSaveQueue();
      }
    }
  }, [getApiToken, isProfileLoading, profileCacheKey, profileLoadError]);

  const queueProfileSave = useCallback((nextDetails: PersonalDetails): void => {
    queuedDetailsRef.current = nextDetails;
    failedDetailsRef.current = null;
    setSaveErrorMsg("");
    void drainProfileSaveQueue();
  }, [drainProfileSaveQueue]);

  const scheduleProfileSave = useCallback((nextDetails: PersonalDetails): void => {
    clearProfileDebounce();
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      queueProfileSave(nextDetails);
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [queueProfileSave]);

  const flushProfileSave = useCallback((): void => {
    clearProfileDebounce();
    queueProfileSave(latestDetailsRef.current);
  }, [queueProfileSave]);

  const retryProfileSave = (): void => {
    queueProfileSave(failedDetailsRef.current || latestDetailsRef.current);
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ): void => {
    const { name, value } = event.target;
    setPersonalDetails((prev) => {
      const next = { ...prev, [name]: value };
      latestDetailsRef.current = next;
      scheduleProfileSave(next);
      return next;
    });
  };

  const handleLogout = (): void => {
    void logout();
  };

  const openEmailChange = (): void => {
    setEmailChangeForm({ newEmail: "" });
    setEmailChangeError("");
    setIsEmailChangeOpen(true);
  };

  const showEmailChangeError = (message: string): void => {
    setEmailChangeError(message);
    window.requestAnimationFrame(() => {
      document.querySelector(".profile-email-change-panel")?.scrollIntoView({ block: "center" });
    });
  };

  const requestFreshLogin = async (): Promise<void> => {
    await loginWithRedirect({
      appState: { returnTo: '/account-settings/profile' },
      authorizationParams: {
        prompt: 'login',
        max_age: 0,
        scope: 'openid profile email offline_access',
      },
    });
  };

  const getEmailChangeErrorMessage = (data: ChangeEmailResponse): string => {
    if (data.errorCode && emailChangeErrorMessages[data.errorCode]) {
      return emailChangeErrorMessages[data.errorCode];
    }
    if (data.supportRequired) {
      return "This email change needs support follow-up.";
    }
    return data.error || "Failed to change email. Try again.";
  };

  const changeEmail = async (): Promise<void> => {
    const newEmail = emailChangeForm.newEmail.trim().toLowerCase();
    setEmailChangeError("");

    if (!newEmail) {
      showEmailChangeError("Enter the new email.");
      return;
    }
    if (newEmail === personalDetails.email.trim().toLowerCase()) {
      showEmailChangeError("New email matches the current account email.");
      return;
    }

    setIsChangingEmail(true);

    try {
      const token = await getApiToken();
      let freshLoginIdToken: string | null = null;

      if (authMethod === 'custom_jwt') {
        freshLoginIdToken = await getFreshLoginIdToken?.();
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/user_profile/change-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...(freshLoginIdToken ? { "X-BotSpot-Fresh-Login-Id-Token": freshLoginIdToken } : {}),
        },
        body: JSON.stringify({
          currentEmail: personalDetails.email,
          newEmail,
          confirmEmail: newEmail,
          ...(freshLoginIdToken ? { freshLoginIdToken } : {}),
        }),
      });

      const data = (await response.json()) as ChangeEmailResponse;
      if (!response.ok) {
        if (data.errorCode === "FRESH_LOGIN_REQUIRED") {
          await requestFreshLogin();
          return;
        }
        showEmailChangeError(getEmailChangeErrorMessage(data));
        return;
      }

      const updatedEmail = data.newEmail || newEmail;
      setPersonalDetails((prev) => {
        const next = { ...prev, email: updatedEmail };
        savedDetailsRef.current = { ...savedDetailsRef.current, email: updatedEmail };
        latestDetailsRef.current = next;
        writeAccountSettingsCache(profileCacheKey, next);
        return next;
      });
      setEmailChangeForm({ newEmail: "" });
      setIsEmailChangeOpen(false);
    } catch {
      showEmailChangeError("Network error. Try again.");
    } finally {
      setIsChangingEmail(false);
    }
  };

  const renderHeader = (): React.ReactElement => (
    <div className="settings-subpage-header user-profile-header">
      <div className="settings-subpage-header-left">
        <h2 className="settings-subpage-title">My Profile</h2>
        <p className="settings-subpage-description text-muted">
          Manage profile details.
        </p>
      </div>
    </div>
  );

  if (isAuthLoading || !isAuthenticated) {
    return (
      <div className="user-profile-page">
        {renderHeader()}
        <AccountSettingsLoadingState
          className="profile-loading-state"
          label="Loading profile"
          text={isAuthLoading ? 'Loading profile...' : 'Redirecting to login...'}
          variant="section"
        />
      </div>
    );
  }

  const profileFieldsDisabled = isProfileLoading || Boolean(profileLoadError);

  return (
    <div className="user-profile-page">
      {renderHeader()}

      {isProfileLoading ? (
        <AccountSettingsLoadingState
          className="profile-loading-state"
          label="Loading profile details"
          text="Loading profile details..."
          variant="section"
        />
      ) : (
        <>
          {profileLoadError && (
            <Alert className="profile-state-row profile-state-row-error" variant="warning">
              <span>{profileLoadError}</span>
              <Button
                type="button"
                variant="outline-primary"
                size="sm"
                onClick={() => fetchProfile(undefined, { force: true })}
              >
                Retry
              </Button>
            </Alert>
          )}

          {saveErrorMsg && (
            <Alert className="profile-state-row profile-state-row-error" variant="danger">
              <span>{saveErrorMsg}</span>
              <Button
                type="button"
                variant="outline-danger"
                size="sm"
                loading={isSavingProfile}
                onClick={retryProfileSave}
              >
                Retry
              </Button>
            </Alert>
          )}

          <form className="profile-form">
            <section className="profile-section" aria-label="Account">
              <div className="profile-form-grid">
                <div className="profile-email-control">
                  <div className="profile-email-row">
                    <Field id="email" label="Email">
                      <Input
                        type="email"
                        name="email"
                        value={personalDetails.email || ""}
                        readOnly
                      />
                    </Field>
                    <Button
                      type="button"
                      variant="outline-primary"
                      size="sm"
                      onClick={openEmailChange}
                      disabled={isEmailChangeOpen || profileFieldsDisabled}
                    >
                      Change email
                    </Button>
                  </div>
                  {isEmailChangeOpen && (
                    <div className="profile-email-change-panel">
                      <div className="profile-email-change-grid">
                        <Field id="newEmail" label="New email">
                          <Input
                            type="email"
                            name="newEmail"
                            value={emailChangeForm.newEmail}
                            disabled={profileFieldsDisabled}
                            onChange={(event) => setEmailChangeForm((prev) => ({
                              ...prev,
                              newEmail: event.target.value,
                            }))}
                            autoComplete="email"
                          />
                        </Field>
                      </div>
                      <div className="profile-email-change-actions">
                        {emailChangeError && (
                          <div className="profile-save-error" role="alert">
                            {emailChangeError}
                          </div>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            setIsEmailChangeOpen(false);
                            setEmailChangeError("");
                          }}
                          disabled={isChangingEmail || profileFieldsDisabled}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          variant="primary"
                          loading={isChangingEmail}
                          onClick={changeEmail}
                          disabled={profileFieldsDisabled}
                        >
                          Update email
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <Field id="nickname" label="Nickname">
                  <Input
                    type="text"
                    name="nickname"
                    value={personalDetails.nickname || ""}
                    disabled={profileFieldsDisabled}
                    onChange={handleInputChange}
                    onBlur={flushProfileSave}
                  />
                </Field>

                <Field id="firstName" label="First name">
                  <Input
                    type="text"
                    name="firstName"
                    value={personalDetails.firstName || ""}
                    disabled={profileFieldsDisabled}
                    onChange={handleInputChange}
                    onBlur={flushProfileSave}
                  />
                </Field>

                <Field id="lastName" label="Last name">
                  <Input
                    type="text"
                    name="lastName"
                    value={personalDetails.lastName || ""}
                    disabled={profileFieldsDisabled}
                    onChange={handleInputChange}
                    onBlur={flushProfileSave}
                  />
                </Field>
              </div>
            </section>

            <section className="profile-section" aria-label="Profile details">
              <div className="profile-form-grid">
                <Field id="phone" label="Phone number">
                  <Input
                    type="tel"
                    name="phone"
                    placeholder="+1 (555) 123-4567"
                    value={personalDetails.phone || ""}
                    disabled={profileFieldsDisabled}
                    onChange={handleInputChange}
                    onBlur={flushProfileSave}
                  />
                </Field>

                <Field id="location" label="Location">
                  <Select
                    name="location"
                    value={personalDetails.location || ""}
                    disabled={profileFieldsDisabled}
                    onChange={handleInputChange}
                    onBlur={flushProfileSave}
                  >
                    <option value="">Select country</option>
                    {countryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field id="tradingExperience" label="Trading experience">
                  <Select
                    name="tradingExperience"
                    value={personalDetails.tradingExperience || ""}
                    disabled={profileFieldsDisabled}
                    onChange={handleInputChange}
                    onBlur={flushProfileSave}
                  >
                    <option value="">Select experience</option>
                    <option value="No experience">No experience</option>
                    <option value="1-3 years">1-3 years</option>
                    <option value="3-5 years">3-5 years</option>
                    <option value="more than 5 years">More than 5 years</option>
                  </Select>
                </Field>
              </div>

              <Field id="bio" label="Bio">
                <Textarea
                  rows={3}
                  name="bio"
                  placeholder="Tell us about yourself..."
                  value={personalDetails.bio || ""}
                  disabled={profileFieldsDisabled}
                  onChange={handleInputChange}
                  onBlur={flushProfileSave}
                />
              </Field>
            </section>

            <section className="profile-section profile-session-section" aria-label="Session">
              <div className="profile-session-row">
                <div>
                  <h3>Session</h3>
                  <p>Sign out of BotSpot on this device.</p>
                </div>
                <Button
                  type="button"
                  variant="outline-danger"
                  onClick={handleLogout}
                >
                  Log out
                </Button>
              </div>
            </section>
          </form>
        </>
      )}
    </div>
  );
};

export default UserProfile;
